/**
 * The single module that knows engine-page URL shapes (routing discipline
 * from sc-76790: mounts and links never compose path strings themselves).
 * Slugs are hardcoded per pilot; a general key→slug rule arrives with
 * auto-mounting (Chunk 5).
 */
type Pilot = {
  // The catalog's create operation says the API accepts a create. It does not
  // say a page exists to send one, so a link needs this instead.
  hasCreatePage?: boolean
  // The sidebar slug this pilot takes over, when it differs from its own.
  replacesNavSlug?: string
  slug: string
}

const pilots: { [key: string]: Pilot } = {
  _3rdpartyByoTwilio: { slug: 'byo-twilio' },
  creditAgencies: { hasCreatePage: true, slug: 'credit-agencies' },
  employees: { hasCreatePage: true, slug: 'employees' },
  keys: { hasCreatePage: true, replacesNavSlug: 'api-keys', slug: 'keys' },
  // No create page: the create body is a variant union, so the form needs the
  // picker from sc-79254.
  loanTypes: { slug: 'loan-types' },
  monitoringServices: { slug: 'monitoring/services' },
  users: { hasCreatePage: true, slug: 'users' },
}

/** Every pilot slug, for mounting the engine-pilot routes as company subroutes. */
export const enginePilotSlugs: Array<string> = Object.values(pilots).map(
  (pilot) => pilot.slug,
)

/** Every pilot with its key, in registry order, for rewriting the sidebar. */
export const enginePilots: Array<Pilot & { key: string }> = Object.entries(
  pilots,
).map(([key, pilot]) => ({ key, ...pilot }))

/** Every resource whose create route is mounted. Pins the mount test. */
export const resourceKeysWithCreatePage: Array<string> = Object.keys(
  pilots,
).filter((key) => pilots[key].hasCreatePage)

export const hasCreatePage = (key: string): boolean =>
  pilots[key]?.hasCreatePage === true

const slugFor = (key: string): string => {
  const slug = pilots[key]?.slug
  if (slug == null) {
    throw new Error(`No pilot slug registered for resource key '${key}'`)
  }
  return slug
}

export const resourceListPath = (
  key: string,
  { companyId }: { companyId: string },
): string => `/companies/${companyId}/${slugFor(key)}`

export const resourceDetailPath = (
  key: string,
  { companyId, id }: { companyId: string; id: string },
): string => `${resourceListPath(key, { companyId })}/${id}`

/**
 * A singleton resource has no list, so its list path is the detail page's own
 * URL and leads nowhere. The company it hangs off is the way out.
 */
export const resourceBackFallbackPath = (
  key: string,
  { companyId, hasList }: { companyId: string; hasList: boolean },
): string =>
  hasList ? resourceListPath(key, { companyId }) : `/companies/${companyId}`

export const resourceCreatePath = (
  key: string,
  { companyId }: { companyId: string },
): string => `${resourceListPath(key, { companyId })}/create`
